import { apiClient } from './client'
import { API_ENDPOINTS } from '@/utils/constants'
import type { ChatSticker, ChatStickerPack } from './chat'

interface ApiEnvelope<T> {
  message?: string
  data?: T
}

function unwrapData<T>(response: { data: ApiEnvelope<T> }, fallbackMessage: string): T {
  if (response.data?.data !== undefined) {
    return response.data.data
  }
  throw new Error(response.data?.message || fallbackMessage)
}

export interface StickerPackPayload {
  code?: string
  title?: string
  is_active?: boolean
  admin_only?: boolean
  sort_order?: number
  /** Полный список тарифов набора; пустой массив снимает все привязки. */
  subscription_plan_ids?: number[]
}

export interface StickerUpdatePayload {
  code?: string
  emoji?: string | null
  is_active?: boolean
  sort_order?: number
}

export interface StickerUploadMeta {
  code: string
  emoji?: string
  sort_order?: number
}

export const adminChatStickersApi = {
  getPacks: async (): Promise<ChatStickerPack[]> => {
    const response = await apiClient.get<ApiEnvelope<ChatStickerPack[]>>(
      API_ENDPOINTS.ADMIN.CHAT_STICKERS.PACKS
    )
    return response.data.data ?? []
  },

  createPack: async (payload: {
    code: string
    title: string
    admin_only?: boolean
    subscription_plan_ids?: number[]
  }): Promise<ChatStickerPack> => {
    const response = await apiClient.post<ApiEnvelope<ChatStickerPack>>(
      API_ENDPOINTS.ADMIN.CHAT_STICKERS.PACKS,
      payload
    )
    return unwrapData(response, '')
  },

  updatePack: async (packId: number, payload: StickerPackPayload): Promise<ChatStickerPack> => {
    const response = await apiClient.patch<ApiEnvelope<ChatStickerPack>>(
      API_ENDPOINTS.ADMIN.CHAT_STICKERS.PACK(packId),
      payload
    )
    return unwrapData(response, '')
  },

  deletePack: async (packId: number): Promise<void> => {
    await apiClient.delete(API_ENDPOINTS.ADMIN.CHAT_STICKERS.PACK(packId))
  },

  uploadSticker: async (
    packId: number,
    file: File,
    meta: StickerUploadMeta
  ): Promise<ChatSticker> => {
    const form = new FormData()
    form.append('file', file)
    form.append('code', meta.code)
    if (meta.emoji) form.append('emoji', meta.emoji)
    if (meta.sort_order !== undefined) form.append('sort_order', String(meta.sort_order))
    const response = await apiClient.post<ApiEnvelope<ChatSticker>>(
      API_ENDPOINTS.ADMIN.CHAT_STICKERS.PACK_STICKERS(packId),
      form
    )
    return unwrapData(response, '')
  },

  /** Полный упорядоченный список id стикеров набора — sort_order по позиции. */
  reorderStickers: async (packId: number, stickerIds: number[]): Promise<ChatStickerPack> => {
    const response = await apiClient.post<ApiEnvelope<ChatStickerPack>>(
      API_ENDPOINTS.ADMIN.CHAT_STICKERS.PACK_STICKERS_REORDER(packId),
      { sticker_ids: stickerIds }
    )
    return unwrapData(response, '')
  },

  updateSticker: async (stickerId: number, payload: StickerUpdatePayload): Promise<ChatSticker> => {
    const response = await apiClient.patch<ApiEnvelope<ChatSticker>>(
      API_ENDPOINTS.ADMIN.CHAT_STICKERS.STICKER(stickerId),
      payload
    )
    return unwrapData(response, '')
  },

  deleteSticker: async (stickerId: number): Promise<void> => {
    await apiClient.delete(API_ENDPOINTS.ADMIN.CHAT_STICKERS.STICKER(stickerId))
  },
}
